import React, { useEffect, useState } from "react";
import { PiggyBank, Trash2 } from "lucide-react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { prefetchModuleChunkByRoute } from "@/lib/moduleChunkPrefetch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cajaBancosAPI } from "@/lib/api";
import { dataRefresh } from "@/lib/dataRefresh";
import { formatDateOnlyEsAR, formatMoneyAr } from "@/lib/utils";
import { Movimiento } from "./MovimientosSection";
import { CAJA_LISTADO_PAGE_SIZE, paginateListado } from "./cajaHistorico";

interface MovimientosListadoProps {
  movimientos: Movimiento[];
  isReadOnly?: boolean;
  onDeleted?: () => void;
}

function liveFondoCodigo(mov: Movimiento): string | null {
  const raw = mov.fondoCodigo ?? mov.fondo_codigo ?? null;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s ? s : null;
}

function looksGenericApiMessage(s: string): boolean {
  const t = s.trim().toLowerCase();
  return (
    !t ||
    t === "bad request" ||
    t === "api error" ||
    t === "error" ||
    t === "dia_cerrado" ||
    t === "borrar_desde_fondos" ||
    t === "usd_sin_disponible"
  );
}

function firstHumanMessage(value: unknown, depth = 0): string {
  if (depth > 4 || value == null) return "";
  if (typeof value === "string") {
    const t = value.trim();
    if (looksGenericApiMessage(t)) return "";
    return t;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const got = firstHumanMessage(item, depth + 1);
      if (got) return got;
    }
    return "";
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    for (const key of ["detail", "message", "fecha", "error"]) {
      const got = firstHumanMessage(o[key], depth + 1);
      if (got) return got;
    }
    for (const v of Object.values(o)) {
      const got = firstHumanMessage(v, depth + 1);
      if (got) return got;
    }
  }
  return "";
}

function movimientoApiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== "object") return fallback;
  const e = error as { message?: unknown; details?: unknown; data?: unknown; error?: unknown };
  return (
    firstHumanMessage(e.details) ||
    firstHumanMessage(e.data) ||
    firstHumanMessage(e.message) ||
    firstHumanMessage(e.error) ||
    fallback
  );
}

const FONDOS_HIGHLIGHT_STORAGE_KEY = "fondosHighlightCodigo";

function goToFondosForCodigo(setLocation: (path: string) => void, codigo: string) {
  try {
    sessionStorage.setItem(FONDOS_HIGHLIGHT_STORAGE_KEY, codigo);
  } catch {
    /* ignore quota / private mode */
  }
  prefetchModuleChunkByRoute("/fondos");
  setLocation("/fondos");
}

export function MovimientosListado({
  movimientos,
  isReadOnly = false,
  onDeleted,
}: MovimientosListadoProps) {
  const [, setLocation] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<Movimiento | null>(null);

  const filteredMovimientos = searchQuery.trim()
    ? movimientos.filter(mov =>
      (mov.usuario_nombre || mov.usuarioNombre || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (mov.codigo || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (mov.tipo_movimiento_nombre || mov.tipoMovimiento || "").toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      (mov.observaciones || "").toLowerCase().includes(searchQuery.toLowerCase())
    )
    : movimientos;

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const paged = paginateListado(filteredMovimientos, page, CAJA_LISTADO_PAGE_SIZE);

  const handleConfirmDelete = async () => {
    const id = deleteTarget?.id;
    if (id == null) return;
    try {
      await cajaBancosAPI.deleteMovimiento(id);
      toast.success("Movimiento eliminado");
      setDeleteTarget(null);
      dataRefresh.emit("cierres");
      onDeleted?.();
    } catch (error) {
      console.error("Error deleting movimiento:", error);
      toast.error(movimientoApiErrorMessage(error, "Error al eliminar el movimiento"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Input
          type="text"
          placeholder="Buscar por usuario, tipo, ID u observaciones..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full"
        />
      </div>

      {filteredMovimientos.length > 0 ? (
        <div className="border border-input rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium">ID/Código</th>
                <th className="text-left py-3 px-4 text-sm font-medium">Fecha</th>
                <th className="text-left py-3 px-4 text-sm font-medium">Tipo de Movimiento</th>
                <th className="text-left py-3 px-4 text-sm font-medium">Usuario</th>
                <th className="text-right py-3 px-4 text-sm font-medium">Importe</th>
                <th className="text-left py-3 px-4 text-sm font-medium">Observaciones</th>
                {!isReadOnly && (
                  <th className="text-center py-3 px-4 text-sm font-medium">Acciones</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paged.items.map((mov) => {
                const fondoCodigo = liveFondoCodigo(mov);
                const canDelete = !isReadOnly && !fondoCodigo && mov.id != null;

                return (
                  <tr
                    key={mov.id || mov.codigo}
                    className="border-t border-input hover:bg-muted/50 transition-colors"
                  >
                    <td className="py-3 px-4 text-sm font-mono">{mov.codigo || mov.id}</td>
                    <td className="py-3 px-4 text-sm">
                      {formatDateOnlyEsAR(mov.fecha)}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className="inline-flex items-center gap-2">
                        {mov.tipoMovimientoNombre || mov.tipo_movimiento_nombre || mov.tipoMovimiento || '-'}
                        {fondoCodigo ? (
                          <Badge variant="secondary" className="rounded-full font-normal">
                            En fondos
                          </Badge>
                        ) : null}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm font-medium">{mov.usuarioNombre || mov.usuario_nombre || "Sin usuario"}</td>
                    <td className="py-3 px-4 text-sm text-right font-semibold">
                      ${formatMoneyAr(mov.importe)}
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {mov.observaciones || "-"}
                    </td>
                    {!isReadOnly && (
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center">
                          {canDelete ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteTarget(mov)}
                              className={`h-8 w-8 p-0 ${deleteIconButtonClass}`}
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : fondoCodigo ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => goToFondosForCodigo(setLocation, fondoCodigo)}
                              onMouseEnter={() => prefetchModuleChunkByRoute("/fondos")}
                              onFocus={() => prefetchModuleChunkByRoute("/fondos")}
                              className="h-8 w-8 p-0"
                              title="Eliminar en Fondos"
                              aria-label="Eliminar en Fondos"
                            >
                              <PiggyBank className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          {searchQuery.trim() ? "No se encontraron resultados" : "No hay datos para mostrar"}
        </div>
      )}

      {filteredMovimientos.length > 0 && (
        <Pagination
          currentPage={paged.page}
          totalCount={paged.total}
          pageSize={CAJA_LISTADO_PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar movimiento</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que deseas eliminar este movimiento? Si hay un cierre
              diario abierto en esa fecha, se quita la línea y se actualizan los
              totales. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleConfirmDelete}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
